import apiConfig from '../../config/api';
import type { User } from '../../types/services/user';
import type { RoleKey } from '../../config/permissions';

const AUTH_API_URL = `${apiConfig.baseUrl}${apiConfig.endpoints.auth}`;
const VERIFY_EMAIL_URL = `${apiConfig.baseUrl}${apiConfig.endpoints.verifyEmail}`;
const RESEND_VERIFICATION_URL = `${apiConfig.baseUrl}${apiConfig.endpoints.resendVerification}`;
const INVITES_API_URL = `${apiConfig.baseUrl}${apiConfig.endpoints.invites}`;
const ACCEPT_INVITE_URL = `${apiConfig.baseUrl}${apiConfig.endpoints.acceptInvite}`;

const TOKEN_KEY = 'authToken';
const USER_KEY = 'authUser';

const getToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

const setToken = (token: string): void => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {}
};

const clearToken = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {}
};

const setUser = (user: User | null): void => {
  try {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  } catch {}
};

const getUser = (): User | null => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
};

export type LoginCredentials = { email: string; password: string };
export type RegisterPayload = { name: string; email: string; password: string };
export type InvitePayload = { email: string; role?: RoleKey; expiresAt?: string };
export type AcceptInvitePayload = { token: string; name?: string; password?: string };

export const login = async (credentials: LoginCredentials): Promise<User> => {
  // Paso 1: login para obtener token JWT
  const loginRes = await fetch(`${AUTH_API_URL}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email: credentials.email, password: credentials.password }),
  });
  const loginJson = await loginRes.json().catch(() => null);
  if (!loginRes.ok || !loginJson) {
    throw new Error(loginJson?.error?.message || 'Credenciales inválidas');
  }

  // Normalización flexible: soporta respuestas con token o con user directo
  const hasToken = !!loginJson?.data?.token;
  const hasUserDirect = !!loginJson?.data?.user;
  const dataLooksLikeUser = !!loginJson?.data && !hasToken && !hasUserDirect && typeof loginJson.data === 'object' && (loginJson.data.email || loginJson.data.id);

  if (hasToken) {
    const token: string = loginJson.data.token as string;
    setToken(token);
    // Paso 2: obtener perfil con /auth/me
    const meRes = await fetch(`${AUTH_API_URL}/me`, {
      method: 'GET',
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    });
    const meJson = await meRes.json().catch(() => null);
    if (!meRes.ok || !meJson || !meJson.data || !meJson.data.user) {
      throw new Error('No se pudo obtener el perfil del usuario');
    }
    const user: User = normalizeUser(meJson.data.user);
    setUser(user);
    return user;
  }

  if (hasUserDirect || dataLooksLikeUser) {
    const rawUser = hasUserDirect ? loginJson.data.user : loginJson.data;
    const user: User = normalizeUser(rawUser);
    setUser(user);
    return user;
  }

  throw new Error('Respuesta de login no válida');
};

export const logout = async (): Promise<void> => {
  // No hay endpoint de logout en backend actual; limpiar estado local
  clearToken();
  setUser(null);
};

export const checkAuth = async (): Promise<User | null> => {
  const token = getToken();
  if (!token) {
    // Si no hay token, pero existe usuario en caché (por login directo), devolverlo
    const cached = getUser();
    if (cached) return cached;
    setUser(null);
    return null;
  }
  const res = await fetch(`${AUTH_API_URL}/me`, {
    method: 'GET',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json || !json.data || !json.data.user) {
    clearToken();
    setUser(null);
    return null;
  }
  const user: User = normalizeUser(json.data.user);
  setUser(user);
  return user;
};

export const register = async (payload: RegisterPayload): Promise<User> => {
  // Crear usuario vía /users y luego login + me
  const token = getToken();
  const res = await fetch(`${apiConfig.baseUrl}${apiConfig.endpoints.users}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json || !json.data) {
    throw new Error(json?.error?.message || 'Error al registrar');
  }
  const created: User = json.data as User;
  // Si no hay token previo, intentar login directo
  if (!getToken()) {
    await login({ email: payload.email, password: payload.password });
  } else {
    setUser(created);
  }
  return created;
};

export const getAuthUser = (): User | null => getUser();

export const verifyEmail = async (token: string): Promise<{ verified: boolean; user: User; email_updated?: boolean; email_update_reason?: string }> => {
  const res = await fetch(VERIFY_EMAIL_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ token }),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json || !json.data) {
    throw new Error(json?.error?.message || 'Error al verificar email');
  }
  const { verified, user, email_updated, email_update_reason } = json.data as { verified: boolean; user: User; email_updated?: boolean; email_update_reason?: string };
  // Actualizar usuario en caché si coincide
  const current = getUser();
  if (current && current.id === user.id) setUser(user);
  return { verified, user, email_updated, email_update_reason };
};

export const createInvite = async (payload: InvitePayload): Promise<{ token: string }> => {
  const token = getToken();
  const res = await fetch(INVITES_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json || !json.data) {
    throw new Error(json?.error?.message || 'Error al crear invitación');
  }
  const invite = json.data as { token: string };
  return { token: invite.token };
};

export const acceptInvite = async (payload: AcceptInvitePayload): Promise<{ user: User; verification_token?: string }> => {
  const res = await fetch(ACCEPT_INVITE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json || !json.data) {
    throw new Error(json?.error?.message || 'Error al aceptar invitación');
  }
  const { user, verification_token } = json.data as { user: any; verification_token?: string };
  // Guardar usuario local si ya autenticado
  const current = getToken();
  const normalized = normalizeUser(user);
  if (current) setUser(normalized);
  return { user: normalized, verification_token };
};

export const resendVerification = async (email?: string): Promise<{ sent: boolean; sentTo?: string; lastHourCount?: number; totalCount?: number }> => {
  const token = getToken();
  const body = email ? { email } : undefined;
  const res = await fetch(RESEND_VERIFICATION_URL, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json || !json.data) {
    throw new Error(json?.error?.message || 'Error al reenviar verificación');
  }
  return json.data as { sent: boolean; sentTo?: string; lastHourCount?: number; totalCount?: number };
};

export default { login, logout, checkAuth, register, getAuthUser, verifyEmail, resendVerification, createInvite, acceptInvite };

// --- Helpers ---
function normalizeUser(raw: any): User {
  const roleStr: string | undefined = raw?.role || raw?.claims?.role;
  const roleMap: Record<string, RoleKey> = {
    admin: 'admin_general',
    admin_general: 'admin_general',
    admin_blog: 'admin_blog',
    admin_comunidad: 'admin_comunidad',
    admin_descuentos: 'admin_descuentos',
    autor: 'autor',
    miembro: 'miembro',
    miembro_estudiante: 'miembro_estudiante',
    miembro_academia: 'miembro_academia',
    miembro_maker: 'miembro_maker',
    moderador: 'moderador',
    soporte: 'soporte',
    invitado: 'invitado',
  };
  const rawRoles: string[] = Array.isArray(raw?.claims?.roles)
    ? (raw.claims.roles as string[])
    : Array.isArray(raw?.roles)
    ? (raw.roles as string[])
    : roleStr
    ? [roleStr]
    : [];
  const mappedRoles: RoleKey[] = Array.from(
    new Set(
      rawRoles
        .map((r) => roleMap[r] || (r as RoleKey))
        .filter((r) => !!r)
    )
  ) as RoleKey[];
  const attributes = (raw?.attributes || raw?.claims?.attributes || {}) as any;
  if (typeof attributes.email_verified === 'string') {
    attributes.email_verified = attributes.email_verified === 'true' || attributes.email_verified === '1';
  }
  const si = attributes.social_instagram;
  const sx = attributes.social_x;
  const sf = attributes.social_facebook;
  const st = attributes.social_tiktok;
  if (si || sx || sf || st) {
    attributes.socials = {
      instagram: si || undefined,
      x: sx || undefined,
      facebook: sf || undefined,
      tiktok: st || undefined,
    };
  }
  if (typeof attributes.engagement_level === 'string') {
    const n = parseInt(attributes.engagement_level, 10);
    if (!isNaN(n) && n >= 1 && n <= 5) attributes.engagement_level = n;
  }
  const user: User = {
    id: String(raw?.id ?? raw?.user_id ?? ''),
    email: String(raw?.email ?? ''),
    name: String(raw?.name ?? raw?.full_name ?? ''),
    status: (raw?.status as any) || 'active',
    claims: {
      roles: mappedRoles,
      attributes: attributes as User['claims']['attributes'],
    },
  };
  return user;
}